// Comments — manage comments on the connected Instagram account's posts:
// read them, add a comment, reply, hide/unhide and delete
// (Meta permission: instagram_manage_comments).
//
// Instagram's API can't edit a comment's text, so the page offers hide/unhide
// or delete-and-post-again instead.

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { smmApi, type InstagramComment, type InstagramMedia, type InstagramMediaResponse } from '@/services/smmApi';
import { DataFreshnessBanner, useBackgroundRefresh } from './DataFreshness';

const MAX_COMMENT_LENGTH = 2200;

const errorMessage = (err: unknown, fallback: string) =>
  (err as { message?: string })?.message || fallback;

const timeAgo = (timestamp?: string) =>
  timestamp ? formatDistanceToNow(new Date(timestamp), { addSuffix: true }) : '';

const mediaThumbnail = (media: InstagramMedia) =>
  media.media_type === 'VIDEO' ? media.thumbnail_url : media.media_url || media.thumbnail_url;

function MediaThumb({ media, className }: { media: InstagramMedia; className: string }) {
  const src = mediaThumbnail(media);
  return src ? (
    <img src={src} alt="" className={`${className} object-cover`} />
  ) : (
    <div className={`${className} bg-gray-100 flex items-center justify-center`}>
      <i className="ri-instagram-line text-gray-400 text-xl" />
    </div>
  );
}

interface CommentRowProps {
  comment: InstagramComment;
  isReply?: boolean;
  busy: boolean;
  onReply?: (comment: InstagramComment, message: string) => Promise<boolean>;
  onToggleHidden: (comment: InstagramComment) => void;
  onDelete: (comment: InstagramComment) => void;
}

function CommentRow({ comment, isReply = false, busy, onReply, onToggleHidden, onDelete }: CommentRowProps) {
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const sendReply = async () => {
    if (!onReply || !replyText.trim()) return;
    if (await onReply(comment, replyText)) {
      setReplyText('');
      setReplyOpen(false);
    }
  };

  const name = comment.username || 'Instagram user';

  return (
    <div className={`flex gap-3 ${isReply ? 'mt-3' : ''}`}>
      <div className={`flex-shrink-0 rounded-full flex items-center justify-center font-semibold text-white ${isReply ? 'w-7 h-7 text-xs' : 'w-9 h-9 text-sm'} ${comment.is_own ? 'bg-[#0859B8]' : 'bg-gray-400'}`}>
        {name.charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center flex-wrap gap-2">
          <span className="text-sm font-semibold text-gray-900">@{name}</span>
          {comment.is_own && (
            <span className="text-[10px] font-semibold bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">Your account</span>
          )}
          {comment.hidden && (
            <span className="text-[10px] font-semibold bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded flex items-center gap-0.5">
              <i className="ri-eye-off-line" /> Hidden on Instagram
            </span>
          )}
          <span className="text-xs text-gray-400">{timeAgo(comment.timestamp)}</span>
        </div>
        <p className={`text-sm mt-0.5 whitespace-pre-wrap break-words ${comment.hidden ? 'text-gray-400' : 'text-gray-700'}`}>
          {comment.text}
        </p>

        {confirmDelete ? (
          <div className="mt-2 flex items-center gap-2 text-xs">
            <span className="text-gray-700">Delete this comment from Instagram?</span>
            <button
              onClick={() => { setConfirmDelete(false); onDelete(comment); }}
              disabled={busy}
              className="px-2.5 py-1 rounded-md bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-50 cursor-pointer"
            >
              Yes, delete
            </button>
            <button onClick={() => setConfirmDelete(false)} className="px-2.5 py-1 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer">
              Cancel
            </button>
          </div>
        ) : (
          <div className="mt-1.5 flex items-center gap-4 text-xs font-medium">
            {onReply && (
              <button
                onClick={() => setReplyOpen((open) => !open)}
                disabled={busy}
                title="Reply to this comment on Instagram as your account"
                className="text-gray-500 hover:text-blue-600 disabled:opacity-50 flex items-center gap-1 cursor-pointer"
              >
                <i className="ri-reply-line" /> Reply
              </button>
            )}
            <button
              onClick={() => onToggleHidden(comment)}
              disabled={busy}
              title={comment.hidden ? 'Show this comment on Instagram again' : 'Hide this comment from everyone except its author on Instagram'}
              className="text-gray-500 hover:text-amber-600 disabled:opacity-50 flex items-center gap-1 cursor-pointer"
            >
              <i className={comment.hidden ? 'ri-eye-line' : 'ri-eye-off-line'} /> {comment.hidden ? 'Unhide' : 'Hide'}
            </button>
            <button
              onClick={() => setConfirmDelete(true)}
              disabled={busy}
              title="Delete this comment from Instagram"
              className="text-gray-500 hover:text-red-600 disabled:opacity-50 flex items-center gap-1 cursor-pointer"
            >
              <i className="ri-delete-bin-line" /> Delete
            </button>
            {busy && <i className="ri-loader-4-line animate-spin text-gray-400" />}
          </div>
        )}

        {replyOpen && (
          <div className="mt-2">
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              maxLength={MAX_COMMENT_LENGTH}
              rows={2}
              placeholder={`Reply to @${name}…`}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="mt-1.5 flex items-center gap-2">
              <button
                onClick={sendReply}
                disabled={busy || !replyText.trim()}
                className="px-3 py-1.5 rounded-lg bg-[#0859B8] hover:bg-[#074a9b] text-white text-xs font-medium disabled:opacity-50 cursor-pointer"
              >
                Post reply
              </button>
              <button onClick={() => setReplyOpen(false)} className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 text-xs hover:bg-gray-50 cursor-pointer">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Replies can't be replied to: Instagram threads one level deep. */}
        {!isReply && comment.replies && comment.replies.length > 0 && (
          <div className="mt-2 pl-3 border-l-2 border-gray-100">
            {comment.replies.map((reply) => (
              <CommentRow key={reply.id} comment={reply} isReply busy={busy} onToggleHidden={onToggleHidden} onDelete={onDelete} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function CommentManager() {
  const navigate = useNavigate();
  const [mediaState, setMediaState] = useState<InstagramMediaResponse | null>(null);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [loadingMedia, setLoadingMedia] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [comments, setComments] = useState<InstagramComment[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentsError, setCommentsError] = useState<string | null>(null);
  const [newComment, setNewComment] = useState('');
  const [posting, setPosting] = useState(false);
  // Comment currently being changed on Instagram (disables its buttons).
  const [busyId, setBusyId] = useState<string | null>(null);

  // When the shown comments were read, and whether newer ones are on their way.
  const [commentsFreshness, setCommentsFreshness] = useState<{ updatedAt: string | null; refreshing: boolean }>({
    updatedAt: null,
    refreshing: false,
  });

  // quiet: swap the answer in without the loading state; refresh: Refresh button.
  const loadMedia = useCallback(async (quiet = false, refresh = false) => {
    if (!quiet) setLoadingMedia(true);
    setMediaError(null);
    try {
      const data = await smmApi.getInstagramMedia(12, refresh);
      setMediaState(data);
      if (data.status === 'ok') {
        setSelectedId((current) =>
          current && data.media.some((m) => m.id === current) ? current : data.media[0]?.id ?? null,
        );
      }
    } catch (err) {
      if (!quiet) setMediaError(errorMessage(err, 'Could not load your Instagram posts.'));
    } finally {
      if (!quiet) setLoadingMedia(false);
    }
  }, []);

  const loadComments = useCallback(async (mediaId: string, quiet = false, refresh = false) => {
    if (!quiet) setLoadingComments(true);
    setCommentsError(null);
    try {
      const data = await smmApi.getInstagramComments(mediaId, refresh);
      setComments(data.comments);
      setCommentsFreshness({ updatedAt: data.updated_at ?? null, refreshing: Boolean(data.refreshing) });
    } catch (err) {
      setCommentsError(errorMessage(err, 'Could not load comments for this post.'));
    } finally {
      if (!quiet) setLoadingComments(false);
    }
  }, []);

  useEffect(() => {
    loadMedia();
  }, [loadMedia]);

  useEffect(() => {
    setComments([]);
    setNewComment('');
    if (selectedId) loadComments(selectedId);
  }, [selectedId, loadComments]);

  const media = mediaState?.status === 'ok' ? mediaState.media : [];
  const selected = media.find((m) => m.id === selectedId) || null;

  // Stored lists are shown at once; while newer ones are read from Instagram,
  // re-ask quietly and swap them in.
  const mediaRefreshing = mediaState?.status === 'ok' ? Boolean(mediaState.refreshing) : false;
  useBackgroundRefresh(mediaRefreshing, () => loadMedia(true));
  const freshness = useBackgroundRefresh(commentsFreshness.refreshing, async () => {
    if (selectedId) await loadComments(selectedId, true);
  });
  const refreshNow = async () => {
    await Promise.all([loadMedia(true, true), selectedId ? loadComments(selectedId, true, true) : undefined]);
  };

  const adjustCommentCount = (mediaId: string, delta: number) => {
    setMediaState((state) =>
      state && state.status === 'ok'
        ? {
            ...state,
            media: state.media.map((m) =>
              m.id === mediaId ? { ...m, comments_count: Math.max(0, (m.comments_count || 0) + delta) } : m,
            ),
          }
        : state,
    );
  };

  const postComment = async () => {
    if (!selectedId || !newComment.trim()) return;
    setPosting(true);
    try {
      await smmApi.addInstagramComment(selectedId, newComment);
      toast.success('Comment posted on Instagram');
      setNewComment('');
      adjustCommentCount(selectedId, 1);
      await loadComments(selectedId, true);
    } catch (err) {
      toast.error(errorMessage(err, 'Could not post the comment.'));
    } finally {
      setPosting(false);
    }
  };

  const replyTo = async (comment: InstagramComment, message: string) => {
    if (!selectedId) return false;
    setBusyId(comment.id);
    try {
      await smmApi.replyToInstagramComment(comment.id, message);
      toast.success('Reply posted on Instagram');
      adjustCommentCount(selectedId, 1);
      await loadComments(selectedId, true);
      return true;
    } catch (err) {
      toast.error(errorMessage(err, 'Could not post the reply.'));
      return false;
    } finally {
      setBusyId(null);
    }
  };

  const toggleHidden = async (comment: InstagramComment) => {
    if (!selectedId) return;
    setBusyId(comment.id);
    try {
      await smmApi.setInstagramCommentHidden(comment.id, !comment.hidden);
      toast.success(comment.hidden ? 'Comment is visible on Instagram again' : 'Comment hidden on Instagram');
      await loadComments(selectedId, true);
    } catch (err) {
      toast.error(errorMessage(err, 'Could not update the comment.'));
    } finally {
      setBusyId(null);
    }
  };

  const deleteComment = async (comment: InstagramComment) => {
    if (!selectedId) return;
    setBusyId(comment.id);
    try {
      await smmApi.deleteInstagramComment(comment.id);
      toast.success('Comment deleted from Instagram');
      adjustCommentCount(selectedId, -1 - (comment.replies?.length || 0));
      await loadComments(selectedId, true);
    } catch (err) {
      toast.error(errorMessage(err, 'Could not delete the comment.'));
    } finally {
      setBusyId(null);
    }
  };

  // ── Page states ────────────────────────────────────────────────────────────

  if (loadingMedia && !mediaState) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6 animate-pulse">
        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-16 bg-gray-100 rounded-lg" />)}
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <div className="h-24 bg-gray-100 rounded-lg" />
          <div className="h-12 bg-gray-100 rounded-lg" />
          <div className="h-12 bg-gray-100 rounded-lg" />
        </div>
      </div>
    );
  }

  if (mediaError) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
        <p className="text-sm text-red-600 mb-4">{mediaError}</p>
        <button onClick={() => loadMedia()} className="px-4 py-2 rounded-xl bg-[#0859B8] hover:bg-[#074a9b] text-white text-sm font-medium cursor-pointer">
          Try again
        </button>
      </div>
    );
  }

  if (mediaState && mediaState.status !== 'ok') {
    const expired = mediaState.status === 'reconnect_required';
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-10 text-center max-w-xl mx-auto">
        <div className="w-14 h-14 rounded-full bg-pink-50 flex items-center justify-center mx-auto mb-4">
          <i className="ri-instagram-line text-2xl text-pink-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">
          {expired ? 'Reconnect your Instagram account' : 'Connect your Instagram account'}
        </h3>
        <p className="text-sm text-gray-500 mt-2">{mediaState.message}</p>
        <button
          onClick={() => navigate('/profile')}
          className="mt-5 px-4 py-2 rounded-xl bg-[#0859B8] hover:bg-[#074a9b] text-white text-sm font-medium cursor-pointer"
        >
          Go to Profile
        </button>
      </div>
    );
  }

  const account = mediaState?.status === 'ok' ? mediaState.account : null;

  return (
    <div className="space-y-6">
      <DataFreshnessBanner updatedAt={commentsFreshness.updatedAt} state={freshness} onRefresh={refreshNow} />
      <div className="bg-white rounded-xl border border-gray-200 p-5 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Instagram comments</h2>
          <p className="text-sm text-gray-500">
            Read and manage the comments on your posts: reply, hide, delete or add your own.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {account?.username && (
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-full px-3 py-1">
              <i className="ri-instagram-line text-pink-600" /> @{account.username}
            </span>
          )}
          <button
            onClick={() => { loadMedia(); if (selectedId) loadComments(selectedId); }}
            disabled={loadingMedia}
            title="Reload posts and comments from Instagram"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50 cursor-pointer"
          >
            <i className={`ri-refresh-line ${loadingMedia ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {media.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-sm text-gray-500">
          This Instagram account has no posts yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6 items-start">
          {/* Posts */}
          <div className="bg-white rounded-xl border border-gray-200 p-3">
            <p className="px-2 pt-1 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Recent posts</p>
            <div className="space-y-1 max-h-[640px] overflow-y-auto">
              {media.map((m) => (
                <button
                  key={m.id}
                  onClick={() => setSelectedId(m.id)}
                  className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors cursor-pointer ${
                    m.id === selectedId ? 'bg-blue-50 ring-1 ring-blue-200' : 'hover:bg-gray-50'
                  }`}
                >
                  <MediaThumb media={m} className="w-14 h-14 rounded-md flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-800 line-clamp-2">{m.caption || 'No caption'}</p>
                    <div className="mt-1 flex items-center gap-3 text-xs text-gray-500">
                      <span>{m.timestamp ? new Date(m.timestamp).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : ''}</span>
                      <span className="flex items-center gap-1"><i className="ri-chat-3-line" />{m.comments_count ?? 0}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Comments for the selected post */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            {selected && (
              <>
                <div className="flex gap-4 pb-4 border-b border-gray-100">
                  <MediaThumb media={selected} className="w-20 h-20 rounded-lg flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-800 line-clamp-3 whitespace-pre-wrap">{selected.caption || 'No caption'}</p>
                    {selected.permalink && (
                      <a
                        href={selected.permalink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline"
                      >
                        View on Instagram <i className="ri-external-link-line" />
                      </a>
                    )}
                  </div>
                </div>

                <div className="py-4 border-b border-gray-100">
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Add a comment as @{account?.username || 'your account'}</label>
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    maxLength={MAX_COMMENT_LENGTH}
                    rows={2}
                    placeholder="Write a comment…"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <p className="text-[11px] text-gray-400">
                      Instagram doesn't allow editing a comment's text. To change one, hide it, or delete it and post a new one.
                    </p>
                    <button
                      onClick={postComment}
                      disabled={posting || !newComment.trim()}
                      title="Post this comment on the Instagram post"
                      className="flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#0859B8] hover:bg-[#074a9b] text-white text-sm font-medium disabled:opacity-50 cursor-pointer"
                    >
                      {posting ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-send-plane-line" />} Post comment
                    </button>
                  </div>
                </div>

                <div className="pt-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">Comments</p>
                  {loadingComments ? (
                    <div className="space-y-3 animate-pulse">
                      {[0, 1, 2].map((i) => <div key={i} className="h-12 bg-gray-100 rounded-lg" />)}
                    </div>
                  ) : commentsError ? (
                    <div className="text-sm text-red-600 flex items-center gap-3">
                      {commentsError}
                      <button onClick={() => selectedId && loadComments(selectedId)} className="text-blue-600 hover:underline cursor-pointer">
                        Try again
                      </button>
                    </div>
                  ) : comments.length === 0 ? (
                    <p className="text-sm text-gray-500">No comments on this post yet.</p>
                  ) : (
                    <div className="space-y-5">
                      {comments.map((comment) => (
                        <CommentRow
                          key={comment.id}
                          comment={comment}
                          busy={busyId !== null}
                          onReply={replyTo}
                          onToggleHidden={toggleHidden}
                          onDelete={deleteComment}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
